import { PopdownButton } from '../../../shared/PopdownMenu';
import {
  AcceptButton,
  ArchiveButton,
  DirectDestroyButton,
  EditButton,
  IgnoreButton,
  UnarchiveButton,
  UndoIgnoreButton,
} from './Buttons';
import { DraggableTile } from '../../../shared/DraggableTile';
import { TilePopdownMenu } from './TilePopdownMenu.jsx';
import { BackToPlanButton } from './BackToPlanButton.jsx';
import useHandleTiles from '../../../lib/redux/hooks/useHandleTiles';

const sanitizeDate = (status, date) => {
  if ((status === 'active' || status === 'archive') && date) {
    const splitDate = date.split('T')[0].split('-');
    const dateObject = new Date(Date.UTC(splitDate[0], splitDate[1]));
    return dateObject.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });
  }
};

const ProofTileButtons = ({ isUserSiteAdmin, moveTileBackToPlan, tile }) => {
  if (isUserSiteAdmin) {
    return null;
  }

  return (
    <BackToPlanButton
      key="plan"
      onClick={event => {
        event.stopPropagation();
        moveTileBackToPlan(tile.id, { unschedule: false });
      }}
    />
  );
};

const buttonsPerStatus = {
  active: ({ tile, changeTileStatus }) => [
    <ArchiveButton
      changeTileStatus={changeTileStatus}
      key="archive"
      tile={tile}
    />,
  ],
  archive: ({ tile, changeTileStatus }) => [
    <UnarchiveButton
      changeTileStatus={changeTileStatus}
      key="unarchive"
      tile={tile}
    />,
  ],
  draft: ProofTileButtons,
  user_submitted: ({ tile }) =>
    tile.ignored
      ? [
          <UndoIgnoreButton key="undo_ignore" tileId={tile.id} />,
          <DirectDestroyButton key="destroy" tileId={tile.id} />,
        ]
      : [
          <AcceptButton key="accept" tileId={tile.id} />,
          <IgnoreButton key="ignore" tileId={tile.id} />,
        ],
};

const renderStatInfo = tile => ({
  uniqueViews: tile.uniqueViews,
  views: tile.views,
  completions: tile.completions,
  tileId: tile.id,
});

const TileButtons = ({
  activeStatus,
  tile,
  isUserSiteAdmin,
  moveTileToProof,
  moveTileBackToPlan,
  changeTileStatus,
}) => {
  const { editTile } = useHandleTiles();

  const ButtonsPerStatus = buttonsPerStatus[activeStatus];
  const shouldShowEdit =
    ['draft', 'active', 'archive'].includes(activeStatus) &&
    tile.fullyAssembled;

  return (
    <>
      <ButtonsPerStatus
        isUserSiteAdmin={isUserSiteAdmin}
        moveTileToProof={moveTileToProof}
        tile={tile}
        moveTileBackToPlan={moveTileBackToPlan}
        changeTileStatus={changeTileStatus}
      />
      {shouldShowEdit && (
        <>
          <PopdownButton key="pill" tileId={tile.id} />
          <EditButton
            key="edit"
            onClickAction={() => {
              editTile({
                id: tile.id,
                status: tile.status,
                planDate: tile.planDate,
                editPath: tile.editPath,
              });
            }}
            tileId={tile.id}
            activeStatus={activeStatus}
          />
        </>
      )}
    </>
  );
};

export const SingleTile = ({
  activeFilters,
  activeStatus,
  changeTileStatus,
  handleMenuAction,
  index,
  isUserSiteAdmin,
  moveTileBackToPlan,
  moveTileToProof,
  tile,
}) => {
  return (
    <DraggableTile
      key={tile.id}
      index={index}
      date={
        !tile.fullyAssembled
          ? 'Incomplete'
          : sanitizeDate(activeStatus, tile.activeDate)
      }
      calendarClass={!tile.fullyAssembled ? 'incomplete' : ''}
      tileContainerClass={activeStatus}
      tileThumblinkClass="tile_thumb_link tile_thumb_link_client_admin"
      shadowOverlayButtons={
        <TileButtons
          activeStatus={activeStatus}
          tile={tile}
          moveTileToProof={moveTileToProof}
          isUserSiteAdmin={isUserSiteAdmin}
          moveTileBackToPlan={moveTileBackToPlan}
          changeTileStatus={changeTileStatus}
        />
      }
      popdownMenu={
        <TilePopdownMenu
          activeStatus={activeStatus}
          handleMenuAction={handleMenuAction}
          isUserSiteAdmin={isUserSiteAdmin}
          tile={tile}
        />
      }
      loading={tile.loading}
      tileStats={
        ['active', 'archive'].indexOf(activeStatus) > -1
          ? renderStatInfo(tile)
          : null
      }
      activeFilters={activeFilters}
      {...tile}
      tileShowPath={null}
      planDate={tile.planDate}
      position={tile.position}
    />
  );
};
